import { makeArticle, saintRelated } from "./learnFactory";

export const pageSaintEligibility = makeArticle({
  slug: "who-qualifies-for-saint-tms",
  title: "Is SAINT® TMS Right for Me? Eligibility & Candidacy | Neuro Wellness",
  description:
    "Is SAINT® TMS right for you? FDA-cleared for adults 22+ with major depressive disorder who haven't improved on antidepressants. See the protocol at a glance, who's a good fit, who isn't, and what happens if you're not a candidate.",
  canonical: "/learn/who-qualifies-for-saint-tms",
  h1: "Is SAINT® TMS Right for Me?",
  breadcrumbLabel: "Is SAINT® Right for Me?",
  shortAnswer:
    "SAINT® TMS is FDA-cleared for adults (22 and older) with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. Eligibility also depends on TMS and MRI safety screening — including checks for metal implants, implanted devices, and seizure history — and screening for bipolar disorder, psychosis, and substance use. A psychiatric evaluation determines whether SAINT® is appropriate for a specific person. Individual results vary, and no outcome is guaranteed.",
  keyTakeaways: [
    "SAINT® is FDA-cleared for adults 22+ with MDD who haven't improved on antidepressants.",
    "Eligibility requires TMS and MRI safety screening.",
    "People with certain metal implants or implanted devices are not candidates.",
    "A psychiatric evaluation determines individual eligibility.",
    "No outcome is guaranteed; individual results vary.",
  ],
  sections: [
    {
      heading: "Is SAINT® TMS right for me? Quick answer",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, fMRI-guided accelerated TMS protocol for adults with major depressive disorder whose depression has not improved enough on prior antidepressant medication. If you are 22 or older, have a diagnosis of major depressive disorder, and have not had adequate relief from at least one antidepressant, SAINT® may be an option — after a full psychiatric evaluation and MRI safety screening.",
        "A psychiatrist reviews your diagnosis, medication history, and overall health, and screens for conditions that may affect eligibility (bipolar disorder, psychosis, seizure history, and certain metal implants or devices). If you appear to be a candidate, the next steps are scheduling an fMRI and planning the 5-day treatment week. Individual results vary, and no outcome is guaranteed.",
      ],
    },
    {
      heading: "SAINT® protocol at a glance",
      table: {
        headers: [
          "What it is",
          "Who it's for",
          "How it's delivered",
          "FDA status",
        ],
        rows: [
          [
            "50 sessions of fMRI-guided intermittent theta burst stimulation (iTBS)",
            "Adults 22+ with MDD who haven't improved enough on antidepressants",
            "10 sessions/day for 5 consecutive days, ~10 min each with 50-min breaks",
            "510(k) K220177, cleared September 2022",
          ],
        ],
      },
    },
    {
      heading: "Who may be a good fit",
      paragraphs: ["SAINT® may be a good fit if:"],
      list: [
        "You are an adult (22 or older).",
        "You have been diagnosed with major depressive disorder.",
        "You have tried antidepressant medication without satisfactory improvement.",
        "You can safely undergo a brain MRI. [[Does SAINT\u00ae require an MRI?|/learn/does-saint-tms-require-an-mri]]",
        "You do not have ferromagnetic metal in or near the head.",
        "You do not have implanted electronic stimulators.",
        "You do not have a seizure history that would rule out treatment.",
        "You can commit to 5 consecutive treatment days at a verified site.",
      ],
    },
    {
      heading: "Who isn't a fit",
      paragraphs: ["SAINT® is generally not appropriate for:"],
      list: [
        "People with ferromagnetic metal in or near the head (cochlear implants, aneurysm clips, certain stents).",
        "People with implanted electronic stimulators.",
        "People who cannot safely undergo an MRI.",
        "People with a history of seizures or epilepsy (requires careful evaluation).",
        "People currently in a manic episode.",
        "Anyone under 18 — SAINT® is FDA-cleared for adults only. [[SAINT\u00ae age requirements|/learn/saint-age-requirements]]",
        "People whose primary diagnosis is bipolar depression, PTSD, an anxiety disorder, or OCD (SAINT® is not FDA-cleared for these conditions).",
      ],
    },
    {
      heading: "What happens if I'm not a candidate",
      paragraphs: [
        "If a psychiatric evaluation shows SAINT® is not the right fit — because of an implant, a seizure history, an MRI contraindication, or a diagnosis SAINT® isn't cleared for — that is not the end of the road. Neuro Wellness offers other evidence-based options in the same clinic: conventional TMS, SPRAVATO\u00ae (esketamine), IV ketamine, medication management, and psychotherapy. A psychiatric evaluation can clarify which of these may be appropriate for your situation.",
        "If the reason is a metal implant or MRI contraindication specifically, conventional TMS — which does not require an fMRI — may still be an option, though it uses a different, non-personalized targeting method. We help you weigh the trade-offs honestly, including cost, coverage, and the evidence behind each option. [[Explore other options when treatment isn't working|/learn/options-if-therapy-doesnt-help-depression]].",
      ],
      links: [
        { to: "/am-i-a-candidate", text: "Check if you may be a candidate" },
        { to: "/saint", text: "Learn more about SAINT® at Neuro Wellness" },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
    {
      heading: "Do you need treatment-resistant depression?",
      paragraphs: [
        "SAINT® is FDA-cleared for adults whose depression has not improved on antidepressant medication. This overlaps with common definitions of treatment-resistant depression, which typically requires inadequate response to at least two antidepressant trials of adequate dose and duration.",
        "However, the FDA-cleared indication does not specify a minimum number of failed medication trials. A psychiatrist determines whether your treatment history meets the criteria for SAINT® based on your individual circumstances.",
      ],
    },
    {
      heading: "What this means for patients",
      paragraphs: [
        "Eligibility for SAINT® is determined by a psychiatric evaluation. A psychiatrist reviews your diagnosis, medication history, and overall health, and screens for conditions that may affect eligibility. If you appear to be a candidate, the next steps include scheduling an fMRI and planning the treatment week.",
        "No treatment is guaranteed to work, and individual results vary. The clinical trials showed promising results, but they were small and conducted at a single site. A realistic treatment plan includes a follow-up strategy and what to try next if SAINT® does not provide adequate relief.",
      ],
      links: [
        { to: "/am-i-a-candidate", text: "Check if you may be a candidate" },
        { to: "/saint", text: "Learn more about SAINT® at Neuro Wellness" },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Is SAINT\u00ae TMS right for me?",
      answer:
        "SAINT\u00ae may be right for you if you are 22 or older, have major depressive disorder, and have not had adequate relief from prior antidepressant medication. A psychiatric evaluation confirms eligibility, including MRI and TMS safety screening. Individual results vary, and no outcome is guaranteed.",
    },
    {
      question: "Who qualifies for SAINT\u00ae TMS?",
      answer:
        "SAINT\u00ae is FDA-cleared for adults (22+) with major depressive disorder who have not improved on antidepressant medication. Eligibility also requires TMS and MRI safety screening. A psychiatric evaluation determines whether SAINT\u00ae is appropriate for a specific person. Individual results vary.",
    },
    {
      question:
        "Do I need treatment-resistant depression to receive SAINT\u00ae?",
      answer:
        "SAINT\u00ae is FDA-cleared for adults whose depression has not improved on antidepressant medication, which overlaps with treatment-resistant depression. The FDA indication does not specify a minimum number of failed trials. A psychiatrist determines whether your treatment history meets the criteria.",
    },
    {
      question: "Who should not receive SAINT\u00ae?",
      answer:
        "People with ferromagnetic metal in or near the head, implanted electronic stimulators, or who cannot have an MRI are not candidates. A history of seizures requires careful evaluation. SAINT\u00ae is not FDA-cleared for bipolar depression, PTSD, anxiety disorders alone, OCD, or anyone under 18.",
    },
    {
      question: "What happens if I'm not a candidate for SAINT\u00ae?",
      answer:
        "If SAINT\u00ae isn't a fit — because of an implant, MRI contraindication, or a diagnosis it isn't cleared for — other evidence-based options are available at Neuro Wellness, including conventional TMS, SPRAVATO\u00ae, ketamine, medication management, and psychotherapy. A psychiatric evaluation helps determine which may be appropriate.",
    },
    {
      question: "Can SAINT\u00ae work if antidepressants didn't?",
      answer:
        "SAINT\u00ae may help when antidepressants have not, because it works through magnetic brain stimulation rather than chemical neurotransmitters. It is FDA-cleared specifically for adults whose depression has not improved on medication. Individual results vary, and a psychiatric evaluation determines eligibility.",
    },
    {
      question: "How quickly can I start SAINT\u00ae?",
      answer:
        "After a psychiatric evaluation confirms eligibility, the next steps are scheduling an fMRI and planning the treatment week. Self-pay patients can often start within days. Insurance verification, if applicable, may take longer. Contact Neuro Wellness to discuss timing.",
    },
  ],
  sources: [
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology (September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  relatedLinks: saintRelated,
});
